import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["word"]

  static values = {
    words: String,
    duration: { type: Number, default: 1.25 },
    speed: { type: Number, default: 0.04 },
    pause: { type: Number, default: 1.4 },
  }

  connect() {
    this.words = this.wordsValue
      .split("|")
      .map((w) => w.trim())
      .filter((w) => w.length > 0)
    this.index = 0
    this.isAnimating = false
    this.isStopped = false
    this.flipTimeoutIds = []
    this.slotCount = this.words.reduce((max, word) => Math.max(max, word.length), 0)

    if (!this.hasWordTarget || this.words.length === 0) return
    this.renderWord(this.words[this.index])
    this.scheduleNext()
  }

  disconnect() {
    this.isStopped = true
    if (this.timeoutId) window.clearTimeout(this.timeoutId)
    this.clearFlipTimeouts()
  }

  scheduleNext() {
    if (this.isStopped || this.words.length <= 1) return
    if (this.timeoutId) window.clearTimeout(this.timeoutId)
    this.timeoutId = window.setTimeout(() => this.flipToNext(), this.pauseValue * 1000)
  }

  flipToNext() {
    if (this.isStopped || this.isAnimating || !this.hasWordTarget) return

    this.isAnimating = true
    this.clearFlipTimeouts()

    const currentText = this.words[this.index]
    const nextIndex = (this.index + 1) % this.words.length
    const nextText = this.words[nextIndex]
    const durationMs = this.durationValue * 1000
    const charDurationMs = Math.max(120, Math.min(220, durationMs * 0.35))
    const staggerMs = this.slotCount > 1
      ? Math.max(35, (durationMs - charDurationMs) / (this.slotCount - 1))
      : 0

    this.renderWord(currentText)
    const chars = Array.from(this.wordTarget.querySelectorAll(".statsync-flap-char"))

    for (let i = 0; i < this.slotCount; i += 1) {
      const currentChar = currentText[i] || " "
      const nextChar = nextText[i] || " "
      const tile = chars[i]
      if (!tile || currentChar === nextChar) continue

      const delayMs = i * staggerMs
      const outId = window.setTimeout(() => {
        if (this.isStopped) return
        tile.classList.add("is-flipping-out")
      }, delayMs)

      const inId = window.setTimeout(() => {
        if (this.isStopped) return
        tile.textContent = nextChar === " " ? "\u00A0" : nextChar
        tile.classList.toggle("statsync-flap-char--space", nextChar === " ")
        tile.classList.toggle("statsync-flap-char--unused", i >= nextText.length)
        tile.classList.remove("is-flipping-out")
        tile.classList.add("is-flipping-in")
      }, delayMs + (charDurationMs / 2))

      const cleanupId = window.setTimeout(() => {
        if (this.isStopped) return
        tile.classList.remove("is-flipping-in", "is-flipping-out")
      }, delayMs + charDurationMs + 30)

      this.flipTimeoutIds.push(outId, inId, cleanupId)
    }

    const endId = window.setTimeout(() => {
      if (this.isStopped || !this.hasWordTarget) return

      this.index = nextIndex
      this.renderWord(nextText)
      this.isAnimating = false
      this.scheduleNext()
    }, durationMs)
    this.flipTimeoutIds.push(endId)
  }

  renderWord(word) {
    if (!this.hasWordTarget) return
    this.wordTarget.innerHTML = ""
    const normalizedWord = (word || "").padEnd(this.slotCount, " ")

    for (let i = 0; i < normalizedWord.length; i += 1) {
      const char = normalizedWord[i]
      const tile = document.createElement("span")
      tile.className = "statsync-flap-char"
      if (char === " ") {
        tile.classList.add("statsync-flap-char--space")
        if (i >= (word || "").length) tile.classList.add("statsync-flap-char--unused")
        tile.textContent = "\u00A0"
      } else {
        tile.textContent = char
      }
      this.wordTarget.appendChild(tile)
    }
  }

  clearFlipTimeouts() {
    for (const id of this.flipTimeoutIds || []) window.clearTimeout(id)
    this.flipTimeoutIds = []
  }
}
